import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Send,
  ShieldAlert,
  Zap,
  Check,
  Search,
  RefreshCw,
  Filter,
  UserCheck,
  ChevronRight,
  Download,
  SlidersHorizontal,
  X,
  Inbox,
} from 'lucide-react';
import { analyticsApi, nudgeNotificationApi } from '../../Api/BaseApi';
import { useTheme } from '../../Context/ThemeContext';
import {
  moneyWhole,
  moneyExact,
  num,
  formatPercent,
  formatTimestamp,
  emptyDash,
} from '../../utils/format';
import './index.css';

// "64–79%" likely range; one decimal when narrower than 2 points.
const formatRange = (low, high) => {
  if (low == null || high == null) return '—';
  const digits = high - low < 2 ? 1 : 0;
  return `${low.toFixed(digits)}–${high.toFixed(digits)}%`;
};

const Analytics = () => {
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL Query Param state synchronization (Rule X-4)
  const activeMetric = searchParams.get('metric') || 'exposure';
  const exposureSubTab = searchParams.get('expTab') || 'customer';
  const varSubTab = searchParams.get('varTab') || 'customer';
  const notifFilter = searchParams.get('notifFilter') || 'all';
  const selectedRegion = searchParams.get('region') || 'All';
  const searchQuery = searchParams.get('q') || '';
  const density = searchParams.get('density') || 'default'; // 'compact' | 'default' | 'comfortable'

  // Update query params helper
  const updateQuery = useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([k, v]) => {
          if (v === null || v === undefined || v === '' || (k === 'region' && v === 'All') || (k === 'density' && v === 'default')) {
            next.delete(k);
          } else {
            next.set(k, v);
          }
        });
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Sorting state (Rule T-7)
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null); // 'asc' | 'desc' | null

  // Nudge state tracking: { [notificationId]: 'loading' | 'nudged' }
  const [nudgeStates, setNudgeStates] = useState({});
  const [nudgeAllLoading, setNudgeAllLoading] = useState(false);
  const [nudgeToast, setNudgeToast] = useState(null);

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    analyticsApi()
      .then((res) => {
        setData(res);
        setLastUpdated(new Date());
        setErr(null);
      })
      .catch(() => setErr('Failed to load analytics dashboard data. Please verify your connection or try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Handle column sorting toggle (Rule T-7: Tri-state)
  const handleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortField(null);
      setSortDirection(null);
    }
  };

  const handleNudge = async (notificationId, recipientEmail) => {
    setNudgeStates((prev) => ({ ...prev, [notificationId]: 'loading' }));
    try {
      const res = await nudgeNotificationApi(notificationId);
      setNudgeStates((prev) => ({ ...prev, [notificationId]: 'nudged' }));
      setNudgeToast({
        type: 'success',
        message: res.message || `Nudge sent to ${recipientEmail}`,
      });
      setTimeout(() => setNudgeToast(null), 4000);
    } catch (e) {
      setNudgeStates((prev) => ({ ...prev, [notificationId]: null }));
      setNudgeToast({
        type: 'error',
        message: 'Could not send nudge. Please check connection.',
      });
      setTimeout(() => setNudgeToast(null), 4000);
    }
  };

  const handleNudgeAllPending = async () => {
    const pendingItems = data?.drilldowns?.notifications?.pending || [];
    if (pendingItems.length === 0) return;

    setNudgeAllLoading(true);
    try {
      for (const item of pendingItems.slice(0, 10)) {
        await nudgeNotificationApi(item.id).catch(() => {});
        setNudgeStates((prev) => ({ ...prev, [item.id]: 'nudged' }));
      }
      setNudgeToast({
        type: 'success',
        message: `Nudge notifications dispatched to ${Math.min(pendingItems.length, 10)} pending owners.`,
      });
      setTimeout(() => setNudgeToast(null), 4000);
    } catch (e) {
      setNudgeToast({ type: 'error', message: 'Failed to complete batch nudge.' });
      setTimeout(() => setNudgeToast(null), 4000);
    } finally {
      setNudgeAllLoading(false);
    }
  };

  const drilldowns = useMemo(() => data?.drilldowns || {}, [data]);

  const confidenceLine = useMemo(() => {
    const dist = data?.confidence_distribution || {};
    const parts = ['High', 'Medium', 'Low']
      .filter((l) => dist[l])
      .map((l) => `${num(dist[l])} ${l}`);
    return parts.join(' · ');
  }, [data]);

  // KPI cards definition with clear hierarchy & accent discipline (Rules D-1, D-2, D-3, D-4)
  const cards = useMemo(() => {
    if (!data) return [];
    return [
      {
        id: 'exposure',
        isHero: true, // Rule D-2: Dominant headline metric
        label: 'Total Portfolio Open Exposure',
        value: moneyWhole(data.total_open_exposure),
        subValue: `${num(data.overdue_invoices)} invoices past due`,
        stateClass: '',
        icon: <DollarSign size={22} aria-hidden="true" />,
        drilldownHint: 'Drill down by customer account, reason code & region',
      },
      {
        id: 'overdue',
        isHero: false,
        label: 'Overdue Invoices',
        value: num(data.overdue_invoices),
        subValue: `${moneyWhole(drilldowns?.overdue?.aging_summary?.['90_plus_days']?.amount || 0)} over 90d`,
        stateClass: 'state-warning',
        icon: <Clock size={18} aria-hidden="true" />,
        drilldownHint: 'Break down by aging brackets & past-due days',
      },
      {
        id: 'var',
        isHero: false,
        label: 'Expected Late Exposure',
        value: moneyWhole(data.total_value_at_risk),
        subValue: 'Probability-weighted timing risk',
        stateClass: 'state-danger',
        icon: <AlertTriangle size={18} aria-hidden="true" />,
        drilldownHint: 'Drill down by confidence, reason & geography',
      },
      {
        id: 'savings',
        isHero: false,
        label: 'Collected On Time',
        value: moneyWhole(data.savings_generated),
        subValue: 'Paid by due date after action',
        stateClass: '',
        icon: <CheckCircle2 size={18} aria-hidden="true" />,
        drilldownHint: 'View exposures collected on time after an action',
      },
      {
        id: 'notifications',
        isHero: false,
        label: 'Action Notifications Sent',
        value: num(data.action_notifications_sent),
        subValue: 'Across portfolio disputes',
        stateClass: '',
        icon: <Send size={18} aria-hidden="true" />,
        drilldownHint: 'Track dispute aging, owners & actions',
      },
      {
        id: 'completed',
        isHero: false,
        label: 'Actions Completed',
        value: num(data.actions_completed),
        subValue: 'Remediation completed',
        stateClass: '',
        icon: <UserCheck size={18} aria-hidden="true" />,
        drilldownHint: 'View completed resolutions',
      },
      {
        id: 'pending',
        isHero: false,
        label: 'Actions Pending',
        value: num(data.actions_pending),
        subValue: 'Awaiting stakeholder action',
        stateClass: 'state-warning',
        icon: <Zap size={18} aria-hidden="true" />,
        drilldownHint: 'Prioritized by aging with one-click nudge',
      },
      {
        id: 'not_completed',
        isHero: false,
        label: 'Late After Action',
        value: num(data.actions_not_completed),
        subValue: 'Late or unpaid after action',
        stateClass: 'state-danger',
        icon: <ShieldAlert size={18} aria-hidden="true" />,
        drilldownHint: 'View unresolved exposures',
      },
    ];
  }, [data, drilldowns]);

  // Filtered & Sorted exposure by customer
  const filteredCustomerExposure = useMemo(() => {
    let list = drilldowns?.exposure?.by_customer || [];
    if (selectedRegion !== 'All') {
      list = list.filter((c) => c.region === selectedRegion);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((c) =>
        c.customer_id.toLowerCase().includes(q) ||
        (c.dominant_reason && c.dominant_reason.toLowerCase().includes(q))
      );
    }
    if (sortField && sortDirection) {
      list = [...list].sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];
        if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }
        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [drilldowns, selectedRegion, searchQuery, sortField, sortDirection]);

  // Filtered & Sorted overdue invoices list
  const filteredOverdueInvoices = useMemo(() => {
    let list = drilldowns?.overdue?.invoices || [];
    if (selectedRegion !== 'All') {
      list = list.filter((inv) => inv.region === selectedRegion);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((inv) =>
        inv.invoice_id.toLowerCase().includes(q) ||
        inv.customer_id.toLowerCase().includes(q) ||
        (inv.reason_code && inv.reason_code.toLowerCase().includes(q))
      );
    }
    if (sortField && sortDirection) {
      list = [...list].sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];
        if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }
        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [drilldowns, selectedRegion, searchQuery, sortField, sortDirection]);

  // Filtered & Sorted VaR list
  const filteredCustomerVaR = useMemo(() => {
    let list = drilldowns?.var?.by_customer || [];
    if (selectedRegion !== 'All') {
      list = list.filter((c) => c.region === selectedRegion);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((c) =>
        c.customer_id.toLowerCase().includes(q) ||
        (c.dominant_reason && c.dominant_reason.toLowerCase().includes(q))
      );
    }
    if (sortField && sortDirection) {
      list = [...list].sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];
        if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }
        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [drilldowns, selectedRegion, searchQuery, sortField, sortDirection]);

  // Filtered & Sorted notifications list
  const filteredNotifications = useMemo(() => {
    let list = drilldowns?.notifications?.all || [];
    if (activeMetric === 'pending' || notifFilter === 'pending') {
      list = list.filter((n) => n.is_pending);
    } else if (activeMetric === 'completed' || notifFilter === 'completed') {
      list = list.filter((n) => n.is_completed);
    }
    if (selectedRegion !== 'All') {
      list = list.filter((n) => n.region === selectedRegion);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((n) =>
        n.customer_id.toLowerCase().includes(q) ||
        n.user_name.toLowerCase().includes(q) ||
        n.email.toLowerCase().includes(q) ||
        (n.delay_reason && n.delay_reason.toLowerCase().includes(q))
      );
    }
    if (sortField && sortDirection) {
      list = [...list].sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];
        if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }
        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [drilldowns, activeMetric, notifFilter, selectedRegion, searchQuery, sortField, sortDirection]);

  // Export to CSV Functionality (Rules X-2, D-7)
  const handleExportCsv = () => {
    let csvContent = '';
    const nowIso = new Date().toISOString();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    // Header comment block (Rule X-2: Auditable provenance)
    csvContent += `# Pinaka 4.0 Portfolio Analytics Export\n`;
    csvContent += `# Exported: ${nowIso} (${tz})\n`;
    csvContent += `# Base Currency: USD\n`;
    csvContent += `# Region Filter: ${selectedRegion}\n`;
    csvContent += `# Search Query: ${searchQuery || 'None'}\n`;
    csvContent += `# View: ${activeMetric}\n\n`;

    if (activeMetric === 'exposure' && exposureSubTab === 'customer') {
      csvContent += 'Customer Account,Region,Open Exposure (USD),% of Book,Open Invoices,Dominant Reason,Late Risk (%)\n';
      filteredCustomerExposure.forEach((c) => {
        csvContent += `"${c.customer_id}","${c.region}",${c.open_amount},${c.percentage},${c.invoice_count},"${c.dominant_reason}",${c.late_probability}\n`;
      });
    } else if (activeMetric === 'overdue') {
      csvContent += 'Invoice ID,Customer Account,Region,Amount Due (USD),Due Date,Days Overdue,Aging Bracket,Dispute Reason\n';
      filteredOverdueInvoices.forEach((inv) => {
        csvContent += `"${inv.invoice_id}","${inv.customer_id}","${inv.region}",${inv.amount},"${inv.due_date}",${inv.days_overdue},"${inv.aging_bracket}","${inv.reason_code}"\n`;
      });
    } else if (activeMetric === 'var') {
      csvContent += 'Customer Account,Region,Expected Late Exposure (USD),Open Exposure (USD),Late Risk (%),Likely Range Low (%),Likely Range High (%),Confidence,Risk Tier,Dispute Reason\n';
      filteredCustomerVaR.forEach((c) => {
        csvContent += `"${c.customer_id}","${c.region}",${c.value_at_risk},${c.open_amount},${c.late_probability},${c.late_probability_low ?? ''},${c.late_probability_high ?? ''},"${c.confidence || ''}","${c.risk_tier}","${c.dominant_reason}"\n`;
      });
    } else {
      csvContent += 'Stakeholder,Email,Customer Account,Region,Dispute Reason,Date Sent,Aging (Days),Status,Expected Late Exposure (USD)\n';
      filteredNotifications.forEach((n) => {
        csvContent += `"${n.user_name}","${n.email}","${n.customer_id}","${n.region}","${n.delay_reason}","${n.date_sent}",${n.aging_days},"${n.is_completed ? 'Completed' : 'Pending'}",${n.value_at_risk}\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `pinaka_analytics_${activeMetric}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const densityClass = density === 'compact' ? 'density-compact' : (density === 'comfortable' ? 'density-comfortable' : '');

  return (
    <div className={`analytics-page ${isDarkMode ? 'dark-mode' : 'light-mode'} ${densityClass}`}>
      {/* Toast alert with accessible live region (Rule A-3) */}
      {nudgeToast && (
        <div className={`nudge-toast ${nudgeToast.type}`} role="status" aria-live="polite">
          {nudgeToast.type === 'success' ? <Check size={16} aria-hidden="true" /> : <AlertTriangle size={16} aria-hidden="true" />}
          <span>{nudgeToast.message}</span>
        </div>
      )}

      {/* Header bar */}
      <header className="analytics-header">
        <div className="analytics-title-group">
          <h1>Analytics</h1>
          <p className="analytics-sub">
            <span>Portfolio risk, dispute segmentation &amp; prescriptive action tracking</span>
            {lastUpdated && (
              <span className="freshness-pill" title={formatTimestamp(lastUpdated).full}>
                <Clock size={11} aria-hidden="true" />
                Updated {formatTimestamp(lastUpdated).display}
              </span>
            )}
            {data?.source?.dataset_name && (
              <span className="freshness-pill">
                Source: {data.source.dataset_name}{data.source.demo_data ? ' (demo)' : ''}
              </span>
            )}
          </p>
        </div>

        <div className="analytics-actions">
          <button
            className="btn-action"
            onClick={handleExportCsv}
            disabled={!data}
            title="Download CSV of current table view with provenance metadata"
            aria-label="Export current view to CSV"
          >
            <Download size={14} aria-hidden="true" />
            <span>Export CSV</span>
          </button>
          <button
            className="btn-action"
            onClick={fetchAnalytics}
            title="Synchronize latest analytics data"
            aria-label="Sync analytics data"
          >
            <RefreshCw size={14} className={loading ? 'spinning' : ''} aria-hidden="true" />
            <span>Sync</span>
          </button>
        </div>
      </header>

      {/* Global Filter Bar with Density Toggle (Rules D-6, T-4, F-1) */}
      <div className="analytics-filter-bar">
        <div className="filter-left-group">
          <div className="filter-group" role="group" aria-label="Region filter">
            <span className="filter-label"><Filter size={13} aria-hidden="true" /> Region:</span>
            {['All', 'North America', 'EMEA', 'APAC', 'LATAM'].map((reg) => (
              <button
                key={reg}
                className={`filter-chip ${selectedRegion === reg ? 'active' : ''}`}
                onClick={() => updateQuery({ region: reg })}
                aria-pressed={selectedRegion === reg}
              >
                {reg}
              </button>
            ))}
          </div>

          <div className="filter-group">
            <span className="filter-label"><SlidersHorizontal size={13} aria-hidden="true" /> Density:</span>
            <div className="density-switcher" role="group" aria-label="Table density switcher">
              <button
                className={`density-btn ${density === 'compact' ? 'active' : ''}`}
                onClick={() => updateQuery({ density: 'compact' })}
                title="Compact density (38px rows)"
                aria-pressed={density === 'compact'}
              >
                Compact
              </button>
              <button
                className={`density-btn ${density === 'default' ? 'active' : ''}`}
                onClick={() => updateQuery({ density: 'default' })}
                title="Default density (48px rows)"
                aria-pressed={density === 'default'}
              >
                Default
              </button>
              <button
                className={`density-btn ${density === 'comfortable' ? 'active' : ''}`}
                onClick={() => updateQuery({ density: 'comfortable' })}
                title="Comfortable density (56px rows)"
                aria-pressed={density === 'comfortable'}
              >
                Comfortable
              </button>
            </div>
          </div>
        </div>

        <div className="search-box">
          <label htmlFor="analytics-search" className="sr-only">Filter records by customer account, reason, or owner</label>
          <Search size={14} className="search-icon" aria-hidden="true" />
          <input
            id="analytics-search"
            type="text"
            placeholder="Search customer ID, reason, or owner..."
            value={searchQuery}
            onChange={(e) => updateQuery({ q: e.target.value })}
          />
          {searchQuery && (
            <button
              className="clear-search"
              onClick={() => updateQuery({ q: '' })}
              aria-label="Clear search query"
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Error state (Rule C-3) */}
      {err && (
        <div className="analytics-error-banner" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>{err}</span>
          <button onClick={fetchAnalytics}>Try Again</button>
        </div>
      )}

      {/* Loading Skeleton matching final layout (Rule M-2, T-9) */}
      {loading && !data && (
        <div aria-busy="true" aria-label="Loading analytics dashboard">
          <div className="skeleton-kpi-grid">
            <div className="skeleton-box skeleton-card hero" />
            <div className="skeleton-box skeleton-card" />
            <div className="skeleton-box skeleton-card" />
            <div className="skeleton-box skeleton-card" />
            <div className="skeleton-box skeleton-card" />
            <div className="skeleton-box skeleton-card" />
          </div>
          <div className="skeleton-box skeleton-table" />
        </div>
      )}

      {data && (
        <>
          {/* KPI Cards Grid with Dominant Headline Metric (Rules D-1, D-2, D-3, A-2) */}
          <section className="kpi-section" aria-label="Portfolio Metrics Overview">
            <div className="kpi-grid">
              {cards.map((c) => {
                const isSelected = activeMetric === c.id;
                return (
                  <div
                    key={c.id}
                    className={`kpi-card ${c.isHero ? 'hero-card' : ''} ${c.stateClass} ${isSelected ? 'active' : ''}`}
                    onClick={() => updateQuery({ metric: c.id })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        updateQuery({ metric: c.id });
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    title={c.drilldownHint}
                  >
                    <div className="kpi-header-row">
                      <span className="kpi-label">{c.label}</span>
                      <span className="kpi-icon-pill">{c.icon}</span>
                    </div>
                    <div className="kpi-value font-tabular">{c.value}</div>
                    <div className="kpi-footer-row">
                      <span className="kpi-sub">{c.subValue}</span>
                      <span className="kpi-cue">
                        {isSelected ? 'Viewing' : 'Drill down'} <ChevronRight size={13} aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Interactive Drill-Down Dashboard Container */}
          <section className="drilldown-container" aria-label="Metric Drill Down Detail">
            {/* DRILL DOWN 1: TOTAL PORTFOLIO OPEN EXPOSURE */}
            {activeMetric === 'exposure' && (
              <div className="drilldown-panel">
                <div className="drilldown-header">
                  <div>
                    <h2 className="drilldown-title">
                      Portfolio Open Exposure Breakdown — {moneyWhole(data.total_open_exposure)}
                    </h2>
                    <p className="drilldown-desc">
                      Segmented exposure of all currently unpaid invoices by customer account, dispute reason code, and geography.
                    </p>
                  </div>
                  <div className="tab-pill-group" role="tablist" aria-label="Exposure sub-tabs">
                    <button
                      role="tab"
                      aria-selected={exposureSubTab === 'customer'}
                      className={`tab-pill ${exposureSubTab === 'customer' ? 'active' : ''}`}
                      onClick={() => updateQuery({ expTab: 'customer' })}
                    >
                      By Customer
                    </button>
                    <button
                      role="tab"
                      aria-selected={exposureSubTab === 'reason'}
                      className={`tab-pill ${exposureSubTab === 'reason' ? 'active' : ''}`}
                      onClick={() => updateQuery({ expTab: 'reason' })}
                    >
                      By Delay Reason
                    </button>
                    <button
                      role="tab"
                      aria-selected={exposureSubTab === 'region'}
                      className={`tab-pill ${exposureSubTab === 'region' ? 'active' : ''}`}
                      onClick={() => updateQuery({ expTab: 'region' })}
                    >
                      By Region
                    </button>
                  </div>
                </div>

                {/* Sub-view: Exposure By Customer */}
                {exposureSubTab === 'customer' && (
                  <div className="drilldown-content">
                    {filteredCustomerExposure.length === 0 ? (
                      <div className="empty-state-panel">
                        <div className="empty-icon-wrap"><Inbox size={24} aria-hidden="true" /></div>
                        <h3 className="empty-title">No customer accounts found</h3>
                        <p className="empty-desc">
                          No open exposure records match region "{selectedRegion}" {searchQuery ? `and query "${searchQuery}"` : ''}.
                        </p>
                        <button
                          className="empty-action-btn"
                          onClick={() => updateQuery({ region: 'All', q: '' })}
                        >
                          Clear all filters
                        </button>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="enterprise-table" aria-label="Open exposure by customer table">
                          <thead>
                            <tr>
                              <th scope="col" className="col-text col-sticky-first sortable" onClick={() => handleSort('customer_id')} aria-sort={sortField === 'customer_id' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                Customer Account
                                {sortField === 'customer_id' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                              <th scope="col" className="col-text sortable" onClick={() => handleSort('region')} aria-sort={sortField === 'region' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                Region
                                {sortField === 'region' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                              <th scope="col" className="col-num sortable" onClick={() => handleSort('open_amount')} aria-sort={sortField === 'open_amount' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                Open Exposure
                                {sortField === 'open_amount' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                              <th scope="col" className="col-num sortable" onClick={() => handleSort('percentage')} aria-sort={sortField === 'percentage' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                % of Book
                                {sortField === 'percentage' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                              <th scope="col" className="col-num sortable" onClick={() => handleSort('invoice_count')} aria-sort={sortField === 'invoice_count' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                Invoices
                                {sortField === 'invoice_count' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                              <th scope="col" className="col-text">Dominant Reason</th>
                              <th scope="col" className="col-num sortable" onClick={() => handleSort('late_probability')} aria-sort={sortField === 'late_probability' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                Late Risk
                                {sortField === 'late_probability' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCustomerExposure.map((c, i) => (
                              <tr key={c.customer_id} className="table-row-hover">
                                <td className="col-text col-sticky-first font-semibold">
                                  <span className="rank-badge font-tabular">{i + 1}</span> {c.customer_id}
                                </td>
                                <td className="col-text"><span className="region-pill">{c.region}</span></td>
                                <td className="col-num font-tabular font-bold">{moneyExact(c.open_amount)}</td>
                                <td className="col-num">
                                  <div className="bar-meter-cell">
                                    <div className="bar-meter-track" aria-hidden="true">
                                      <div className="bar-meter-fill" style={{ width: `${Math.min(c.percentage * 3, 100)}%` }} />
                                    </div>
                                    <span className="font-tabular">{formatPercent(c.percentage)}</span>
                                  </div>
                                </td>
                                <td className="col-num font-tabular">{num(c.invoice_count)}</td>
                                <td className="col-text"><span className="reason-tag">{emptyDash(c.dominant_reason)}</span></td>
                                <td className="col-num">
                                  <span className={`risk-pill font-tabular ${c.late_probability >= 70 ? 'risk-high' : (c.late_probability >= 40 ? 'risk-med' : 'risk-low')}`}>
                                    {formatPercent(c.late_probability)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="table-summary-row">
                              <td className="col-text col-sticky-first">Total Portfolio Exposure</td>
                              <td className="col-text">—</td>
                              <td className="col-num font-tabular">{moneyExact(filteredCustomerExposure.reduce((acc, c) => acc + (c.open_amount || 0), 0))}</td>
                              <td className="col-num font-tabular">{formatPercent(filteredCustomerExposure.reduce((acc, c) => acc + (c.percentage || 0), 0))}</td>
                              <td className="col-num font-tabular">{num(filteredCustomerExposure.reduce((acc, c) => acc + (c.invoice_count || 0), 0))}</td>
                              <td className="col-text">—</td>
                              <td className="col-num">—</td>
                            </tr>
                          </tfoot>
                        </table>
                        <div className="table-meta-bar">
                          <span>Showing 1–{filteredCustomerExposure.length} of {filteredCustomerExposure.length} accounts</span>
                          <span>Base currency: USD</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Sub-view: Exposure By Reason Code */}
                {exposureSubTab === 'reason' && (
                  <div className="drilldown-content">
                    <div className="segmented-bars-card">
                      <h3 className="section-subtitle">Dispute Reason Distribution</h3>
                      <div className="reason-progress-list">
                        {(drilldowns?.exposure?.by_reason || []).map((r) => (
                          <div key={r.reason_code} className="reason-progress-item">
                            <div className="reason-header">
                              <span className="reason-name">{r.reason_code}</span>
                              <span className="reason-numbers font-tabular">
                                <strong>{moneyExact(r.amount)}</strong> ({formatPercent(r.percentage)} of exposure)
                              </span>
                            </div>
                            <div className="meter-track" aria-hidden="true">
                              <div className="meter-fill" style={{ width: `${Math.min(r.percentage, 100)}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Sub-view: Exposure By Region */}
                {exposureSubTab === 'region' && (
                  <div className="drilldown-content">
                    <div className="cards-split-grid">
                      {(drilldowns?.exposure?.by_region || []).map((reg) => (
                        <div key={reg.region} className="region-stat-card">
                          <div className="region-card-header">
                            <h4>{reg.region}</h4>
                            <span className="badge-share font-tabular">{formatPercent(reg.percentage)} share</span>
                          </div>
                          <div className="region-metric font-tabular">{moneyWhole(reg.amount)}</div>
                          <div className="region-meta">
                            <span className="font-tabular">{num(reg.count)} open invoices</span>
                            <span className="font-tabular">ELE: {moneyWhole(reg.value_at_risk)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* DRILL DOWN 2: OVERDUE INVOICES */}
            {activeMetric === 'overdue' && (
              <div className="drilldown-panel">
                <div className="drilldown-header">
                  <div>
                    <h2 className="drilldown-title">
                      Overdue Invoices Drill-Down — {num(data.overdue_invoices)} Past Due
                    </h2>
                    <p className="drilldown-desc">
                      Detailed invoice attributes, days past due, aging brackets, and amounts requiring urgent settlement.
                    </p>
                  </div>
                </div>

                {/* Aging Summary Brackets */}
                <div className="aging-buckets-grid">
                  <div className="bucket-card">
                    <span className="bucket-label">1–30 Days Overdue</span>
                    <span className="bucket-value font-tabular">{moneyWhole(drilldowns?.overdue?.aging_summary?.['1_30_days']?.amount || 0)}</span>
                    <span className="bucket-sub font-tabular">{num(drilldowns?.overdue?.aging_summary?.['1_30_days']?.count || 0)} invoices</span>
                  </div>
                  <div className="bucket-card">
                    <span className="bucket-label">31–60 Days Overdue</span>
                    <span className="bucket-value font-tabular">{moneyWhole(drilldowns?.overdue?.aging_summary?.['31_60_days']?.amount || 0)}</span>
                    <span className="bucket-sub font-tabular">{num(drilldowns?.overdue?.aging_summary?.['31_60_days']?.count || 0)} invoices</span>
                  </div>
                  <div className="bucket-card alert-amber">
                    <span className="bucket-label">61–90 Days Overdue</span>
                    <span className="bucket-value font-tabular">{moneyWhole(drilldowns?.overdue?.aging_summary?.['61_90_days']?.amount || 0)}</span>
                    <span className="bucket-sub font-tabular">{num(drilldowns?.overdue?.aging_summary?.['61_90_days']?.count || 0)} invoices</span>
                  </div>
                  <div className="bucket-card alert-red">
                    <span className="bucket-label">90+ Days · Credit-Loss Watchlist</span>
                    <span className="bucket-value font-tabular">{moneyWhole(drilldowns?.overdue?.aging_summary?.['90_plus_days']?.amount || 0)}</span>
                    <span className="bucket-sub font-tabular">{num(drilldowns?.overdue?.aging_summary?.['90_plus_days']?.count || 0)} invoices · {formatPercent(data.credit_loss_watchlist?.share_of_open_pct || 0)} of open AR</span>
                  </div>
                </div>

                <div className="drilldown-content">
                  {filteredOverdueInvoices.length === 0 ? (
                    <div className="empty-state-panel">
                      <div className="empty-icon-wrap"><Inbox size={24} aria-hidden="true" /></div>
                      <h3 className="empty-title">No overdue invoices found</h3>
                      <p className="empty-desc">
                        No past-due invoices match region "{selectedRegion}" {searchQuery ? `and query "${searchQuery}"` : ''}.
                      </p>
                      <button
                        className="empty-action-btn"
                        onClick={() => updateQuery({ region: 'All', q: '' })}
                      >
                        Clear all filters
                      </button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="enterprise-table" aria-label="Overdue invoices table">
                        <thead>
                          <tr>
                            <th scope="col" className="col-text col-sticky-first sortable" onClick={() => handleSort('invoice_id')} aria-sort={sortField === 'invoice_id' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Invoice ID
                              {sortField === 'invoice_id' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-text sortable" onClick={() => handleSort('customer_id')} aria-sort={sortField === 'customer_id' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Customer Account
                              {sortField === 'customer_id' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-text sortable" onClick={() => handleSort('region')} aria-sort={sortField === 'region' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Region
                              {sortField === 'region' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-num sortable" onClick={() => handleSort('amount')} aria-sort={sortField === 'amount' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Amount Due
                              {sortField === 'amount' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-text sortable" onClick={() => handleSort('due_date')} aria-sort={sortField === 'due_date' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Due Date
                              {sortField === 'due_date' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-num sortable" onClick={() => handleSort('days_overdue')} aria-sort={sortField === 'days_overdue' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Days Overdue
                              {sortField === 'days_overdue' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-text">Aging Bracket</th>
                            <th scope="col" className="col-text">Dispute Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredOverdueInvoices.map((inv) => (
                            <tr key={inv.invoice_id} className="table-row-hover">
                              <td className="col-text col-sticky-first font-mono font-semibold">{inv.invoice_id}</td>
                              <td className="col-text font-semibold">{inv.customer_id}</td>
                              <td className="col-text"><span className="region-pill">{inv.region}</span></td>
                              <td className="col-num font-tabular font-bold">{moneyExact(inv.amount)}</td>
                              <td className="col-text font-tabular">{inv.due_date}</td>
                              <td className="col-num">
                                <span className={`aging-pill font-tabular ${inv.days_overdue > 90 ? 'critical' : (inv.days_overdue > 60 ? 'warning' : 'moderate')}`}>
                                  {num(inv.days_overdue)} days
                                </span>
                              </td>
                              <td className="col-text"><span className="bracket-tag">{inv.aging_bracket}</span></td>
                              <td className="col-text"><span className="reason-tag">{emptyDash(inv.reason_code)}</span></td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="table-summary-row">
                            <td className="col-text col-sticky-first">Total Overdue</td>
                            <td className="col-text">—</td>
                            <td className="col-text">—</td>
                            <td className="col-num font-tabular">{moneyExact(filteredOverdueInvoices.reduce((acc, inv) => acc + (inv.amount || 0), 0))}</td>
                            <td className="col-text">—</td>
                            <td className="col-num font-tabular">{Math.round(filteredOverdueInvoices.reduce((acc, inv) => acc + (inv.days_overdue || 0), 0) / (filteredOverdueInvoices.length || 1))} avg days</td>
                            <td className="col-text">—</td>
                            <td className="col-text">—</td>
                          </tr>
                        </tfoot>
                      </table>
                      <div className="table-meta-bar">
                        <span>Showing 1–{filteredOverdueInvoices.length} of {filteredOverdueInvoices.length} overdue invoices</span>
                        <span>Base currency: USD</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* DRILL DOWN 3: TOTAL VALUE AT RISK */}
            {activeMetric === 'var' && (
              <div className="drilldown-panel">
                <div className="drilldown-header">
                  <div>
                    <h2 className="drilldown-title">
                      Expected Late Exposure — {moneyWhole(data.total_value_at_risk)}
                    </h2>
                    <p className="drilldown-desc">
                      Open receivables weighted by each invoice's calibrated late-payment probability. A cash-timing risk, not a credit loss.
                    </p>
                    {data.total_cost_of_delay != null && (
                      <p className="drilldown-desc">
                        Estimated cost of delay: <strong className="font-tabular">{moneyWhole(data.total_cost_of_delay)}</strong> — financing cost of late cash at the {data.cost_of_capital_source || `${formatPercent((data.cost_of_capital || 0) * 100)} annual rate`}.
                      </p>
                    )}
                    {confidenceLine && (
                      <p className="drilldown-desc">Prediction confidence on open invoices: {confidenceLine}.</p>
                    )}
                  </div>
                  <div className="tab-pill-group" role="tablist" aria-label="Expected Late Exposure sub-tabs">
                    <button
                      role="tab"
                      aria-selected={varSubTab === 'customer'}
                      className={`tab-pill ${varSubTab === 'customer' ? 'active' : ''}`}
                      onClick={() => updateQuery({ varTab: 'customer' })}
                    >
                      By Customer
                    </button>
                    <button
                      role="tab"
                      aria-selected={varSubTab === 'reason'}
                      className={`tab-pill ${varSubTab === 'reason' ? 'active' : ''}`}
                      onClick={() => updateQuery({ varTab: 'reason' })}
                    >
                      By Delay Reason
                    </button>
                    <button
                      role="tab"
                      aria-selected={varSubTab === 'region'}
                      className={`tab-pill ${varSubTab === 'region' ? 'active' : ''}`}
                      onClick={() => updateQuery({ varTab: 'region' })}
                    >
                      By Region
                    </button>
                  </div>
                </div>

                {varSubTab === 'customer' && (
                  <div className="drilldown-content">
                    {filteredCustomerVaR.length === 0 ? (
                      <div className="empty-state-panel">
                        <div className="empty-icon-wrap"><Inbox size={24} aria-hidden="true" /></div>
                        <h3 className="empty-title">No customer records found</h3>
                        <p className="empty-desc">
                          No accounts match region "{selectedRegion}" {searchQuery ? `and query "${searchQuery}"` : ''}.
                        </p>
                        <button
                          className="empty-action-btn"
                          onClick={() => updateQuery({ region: 'All', q: '' })}
                        >
                          Clear all filters
                        </button>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="enterprise-table" aria-label="Expected late exposure by customer table">
                          <thead>
                            <tr>
                              <th scope="col" className="col-text col-sticky-first sortable" onClick={() => handleSort('customer_id')} aria-sort={sortField === 'customer_id' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                Customer Account
                                {sortField === 'customer_id' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                              <th scope="col" className="col-text sortable" onClick={() => handleSort('region')} aria-sort={sortField === 'region' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                Region
                                {sortField === 'region' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                              <th scope="col" className="col-num sortable" onClick={() => handleSort('value_at_risk')} aria-sort={sortField === 'value_at_risk' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                Expected Late Exposure
                                {sortField === 'value_at_risk' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                              <th scope="col" className="col-num sortable" onClick={() => handleSort('open_amount')} aria-sort={sortField === 'open_amount' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                Open Exposure
                                {sortField === 'open_amount' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                              <th scope="col" className="col-num sortable" onClick={() => handleSort('late_probability')} aria-sort={sortField === 'late_probability' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                P(Late)
                                {sortField === 'late_probability' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                              </th>
                              <th scope="col" className="col-text">Risk Tier</th>
                              <th scope="col" className="col-text">Confidence</th>
                              <th scope="col" className="col-text">Predicted Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCustomerVaR.map((c, idx) => (
                              <tr key={c.customer_id} className="table-row-hover">
                                <td className="col-text col-sticky-first font-semibold">
                                  <span className="rank-badge font-tabular">{idx + 1}</span> {c.customer_id}
                                </td>
                                <td className="col-text"><span className="region-pill">{c.region}</span></td>
                                <td className="col-num font-tabular font-bold">{moneyExact(c.value_at_risk)}</td>
                                <td className="col-num font-tabular">{moneyExact(c.open_amount)}</td>
                                <td className="col-num font-tabular font-bold">{formatPercent(c.late_probability)}</td>
                                <td className="col-text">
                                  <span className={`risk-pill ${c.risk_tier === 'High' ? 'risk-high' : (c.risk_tier === 'Medium' ? 'risk-med' : 'risk-low')}`}>
                                    {c.risk_tier} Risk
                                  </span>
                                </td>
                                <td className="col-text">
                                  {c.confidence ? (
                                    <span
                                      className="reason-tag"
                                      title={`Likely range ${formatRange(c.late_probability_low, c.late_probability_high)} (Venn-Abers calibration)`}
                                    >
                                      {c.confidence}
                                    </span>
                                  ) : emptyDash(null)}
                                </td>
                                <td className="col-text"><span className="reason-tag">{emptyDash(c.dominant_reason)}</span></td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="table-summary-row">
                              <td className="col-text col-sticky-first">Total Expected Late Exposure</td>
                              <td className="col-text">—</td>
                              <td className="col-num font-tabular">{moneyExact(filteredCustomerVaR.reduce((acc, c) => acc + (c.value_at_risk || 0), 0))}</td>
                              <td className="col-num font-tabular">{moneyExact(filteredCustomerVaR.reduce((acc, c) => acc + (c.open_amount || 0), 0))}</td>
                              <td className="col-num font-tabular">{formatPercent(filteredCustomerVaR.reduce((acc, c) => acc + (c.late_probability || 0), 0) / (filteredCustomerVaR.length || 1))} avg</td>
                              <td className="col-text">—</td>
                              <td className="col-text">—</td>
                              <td className="col-text">—</td>
                            </tr>
                          </tfoot>
                        </table>
                        <div className="table-meta-bar">
                          <span>Showing 1–{filteredCustomerVaR.length} of {filteredCustomerVaR.length} accounts</span>
                          <span>Base currency: USD</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {varSubTab === 'reason' && (
                  <div className="drilldown-content">
                    <div className="segmented-bars-card">
                      <h3 className="section-subtitle">Risk-Weighted Exposure by Dispute Reason</h3>
                      <div className="reason-progress-list">
                        {(drilldowns?.var?.by_reason || []).map((r) => (
                          <div key={r.reason_code} className="reason-progress-item">
                            <div className="reason-header">
                              <span className="reason-name">{r.reason_code}</span>
                              <span className="reason-numbers font-tabular">
                                <strong>{moneyExact(r.value_at_risk)}</strong> ELE ({formatPercent(r.percentage)} share)
                              </span>
                            </div>
                            <div className="meter-track" aria-hidden="true">
                              <div className="meter-fill risk-fill" style={{ width: `${Math.min(r.percentage, 100)}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {varSubTab === 'region' && (
                  <div className="drilldown-content">
                    <div className="cards-split-grid">
                      {(drilldowns?.var?.by_region || []).map((reg) => (
                        <div key={reg.region} className="region-stat-card">
                          <div className="region-card-header">
                            <h4>{reg.region}</h4>
                            <span className="badge-share text-red font-tabular">{formatPercent(reg.percentage)} ELE</span>
                          </div>
                          <div className="region-metric font-tabular">{moneyWhole(reg.value_at_risk)}</div>
                          <div className="region-meta">
                            <span>Expected late exposure</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* DRILL DOWN 4: SAVINGS GENERATED */}
            {activeMetric === 'savings' && (
              <div className="drilldown-panel">
                <div className="drilldown-header">
                  <div>
                    <h2 className="drilldown-title">
                      Collected On Time — {moneyWhole(data.savings_generated)}
                    </h2>
                    <p className="drilldown-desc">
                      Expected Late Exposure on invoices paid by their due date after an action notification was completed.
                    </p>
                  </div>
                </div>

                <div className="savings-hero-card">
                  <div className="hero-stat-circle">
                    <CheckCircle2 size={36} aria-hidden="true" />
                  </div>
                  <div className="hero-stat-details">
                    <span className="hero-sub">Exposure Collected On Time After Action</span>
                    <h3 className="hero-number font-tabular">{moneyWhole(data.savings_generated)}</h3>
                    <p className="hero-caption">
                      {num(data.actions_completed)} actions completed to date; the tied invoices were paid on or before their due date.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* DRILL DOWN 5, 6 & 7: ACTION NOTIFICATIONS, COMPLETED & PENDING WITH NUDGE */}
            {(activeMetric === 'notifications' || activeMetric === 'pending' || activeMetric === 'completed' || activeMetric === 'not_completed') && (
              <div className="drilldown-panel">
                <div className="drilldown-header">
                  <div>
                    <h2 className="drilldown-title">
                      {activeMetric === 'pending' ? 'Actions Pending — Awaiting Resolution' :
                       activeMetric === 'completed' ? 'Actions Completed — Remediation Details' :
                       'Action Notifications Sent — Prioritized by Aging'}
                    </h2>
                    <p className="drilldown-desc">
                      Prioritized stakeholder view. Oldest un-acted notifications are prioritized at the top with one-click Nudge functionality.
                    </p>
                  </div>

                  <div className="header-actions-row">
                    <div className="tab-pill-group" role="tablist" aria-label="Notification status filter">
                      <button
                        role="tab"
                        aria-selected={notifFilter === 'all' && activeMetric === 'notifications'}
                        className={`tab-pill ${notifFilter === 'all' && activeMetric === 'notifications' ? 'active' : ''}`}
                        onClick={() => updateQuery({ notifFilter: 'all', metric: 'notifications' })}
                      >
                        All ({num(drilldowns?.notifications?.all?.length || 0)})
                      </button>
                      <button
                        role="tab"
                        aria-selected={activeMetric === 'pending' || notifFilter === 'pending'}
                        className={`tab-pill ${activeMetric === 'pending' || notifFilter === 'pending' ? 'active' : ''}`}
                        onClick={() => updateQuery({ notifFilter: 'pending', metric: 'pending' })}
                      >
                        Pending ({num(drilldowns?.notifications?.pending?.length || 0)})
                      </button>
                      <button
                        role="tab"
                        aria-selected={activeMetric === 'completed' || notifFilter === 'completed'}
                        className={`tab-pill ${activeMetric === 'completed' || notifFilter === 'completed' ? 'active' : ''}`}
                        onClick={() => updateQuery({ notifFilter: 'completed', metric: 'completed' })}
                      >
                        Completed ({num(drilldowns?.notifications?.completed?.length || 0)})
                      </button>
                    </div>

                    {(activeMetric === 'pending' || notifFilter === 'pending' || activeMetric === 'notifications') && (
                      <button
                        className="nudge-all-btn"
                        onClick={handleNudgeAllPending}
                        disabled={nudgeAllLoading || (drilldowns?.notifications?.pending || []).length === 0}
                        title="Send reminders to all pending dispute owners"
                      >
                        <Zap size={14} aria-hidden="true" />
                        <span>{nudgeAllLoading ? 'Nudging...' : 'Nudge Pending'}</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="drilldown-content">
                  {filteredNotifications.length === 0 ? (
                    <div className="empty-state-panel">
                      <div className="empty-icon-wrap"><Inbox size={24} aria-hidden="true" /></div>
                      <h3 className="empty-title">No notifications found</h3>
                      <p className="empty-desc">
                        No notifications match region "{selectedRegion}" {searchQuery ? `and query "${searchQuery}"` : ''}.
                      </p>
                      <button
                        className="empty-action-btn"
                        onClick={() => updateQuery({ region: 'All', q: '', notifFilter: 'all' })}
                      >
                        Clear all filters
                      </button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="enterprise-table" aria-label="Action notifications table">
                        <thead>
                          <tr>
                            <th scope="col" className="col-text col-sticky-first sortable" onClick={() => handleSort('user_name')} aria-sort={sortField === 'user_name' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Stakeholder / Owner
                              {sortField === 'user_name' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-text sortable" onClick={() => handleSort('customer_id')} aria-sort={sortField === 'customer_id' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Customer Account
                              {sortField === 'customer_id' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-text sortable" onClick={() => handleSort('region')} aria-sort={sortField === 'region' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Region
                              {sortField === 'region' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-text">Dispute Reason</th>
                            <th scope="col" className="col-text sortable" onClick={() => handleSort('date_sent')} aria-sort={sortField === 'date_sent' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Date Sent
                              {sortField === 'date_sent' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-num sortable" onClick={() => handleSort('aging_days')} aria-sort={sortField === 'aging_days' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Aging
                              {sortField === 'aging_days' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-center">Status</th>
                            <th scope="col" className="col-num sortable" onClick={() => handleSort('value_at_risk')} aria-sort={sortField === 'value_at_risk' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                              Exposure at Stake
                              {sortField === 'value_at_risk' && <span className="sort-indicator">{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                            </th>
                            <th scope="col" className="col-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredNotifications.map((n) => {
                            const isNudged = nudgeStates[n.id] === 'nudged';
                            const isNudging = nudgeStates[n.id] === 'loading';

                            return (
                              <tr key={n.id} className="table-row-hover">
                                <td className="col-text col-sticky-first">
                                  <div className="owner-cell">
                                    <span className="owner-name">{n.user_name}</span>
                                    <span className="owner-email">{n.email}</span>
                                  </div>
                                </td>
                                <td className="col-text font-semibold">{n.customer_id}</td>
                                <td className="col-text"><span className="region-pill">{n.region}</span></td>
                                <td className="col-text"><span className="reason-tag">{emptyDash(n.delay_reason)}</span></td>
                                <td className="col-text font-tabular">{n.date_sent}</td>
                                <td className="col-num">
                                  <span className={`aging-pill font-tabular ${n.aging_days > 20 ? 'critical' : (n.aging_days > 10 ? 'warning' : 'moderate')}`}>
                                    {num(n.aging_days)}d ago
                                  </span>
                                </td>
                                <td className="col-center">
                                  {n.is_completed ? (
                                    <span className="status-badge completed">
                                      <Check size={12} aria-hidden="true" /> Completed
                                    </span>
                                  ) : (
                                    <span className="status-badge pending">
                                      <Clock size={12} aria-hidden="true" /> Pending
                                    </span>
                                  )}
                                </td>
                                <td className="col-num font-tabular font-bold">{moneyWhole(n.value_at_risk)}</td>
                                <td className="col-center">
                                  {n.is_completed ? (
                                    <span className="status-badge completed" title={n.completed_note || 'Resolved'}>
                                      Resolved
                                    </span>
                                  ) : (
                                    <button
                                      className={`nudge-action-btn ${isNudged ? 'nudged' : ''}`}
                                      onClick={() => handleNudge(n.id, n.email)}
                                      disabled={isNudging || isNudged}
                                      title="Send nudge reminder to stakeholder"
                                      aria-label={`Send nudge reminder to ${n.user_name}`}
                                    >
                                      {isNudging ? (
                                        <RefreshCw size={12} className="spinning" aria-hidden="true" />
                                      ) : isNudged ? (
                                        <>
                                          <Check size={12} aria-hidden="true" />
                                          <span>Nudged</span>
                                        </>
                                      ) : (
                                        <>
                                          <Zap size={12} aria-hidden="true" />
                                          <span>Nudge</span>
                                        </>
                                      )}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="table-summary-row">
                            <td className="col-text col-sticky-first">Total Notifications</td>
                            <td className="col-text">—</td>
                            <td className="col-text">—</td>
                            <td className="col-text">—</td>
                            <td className="col-text">—</td>
                            <td className="col-num font-tabular">{Math.round(filteredNotifications.reduce((acc, n) => acc + (n.aging_days || 0), 0) / (filteredNotifications.length || 1))} avg days</td>
                            <td className="col-center">—</td>
                            <td className="col-num font-tabular">{moneyWhole(filteredNotifications.reduce((acc, n) => acc + (n.value_at_risk || 0), 0))}</td>
                            <td className="col-center">—</td>
                          </tr>
                        </tfoot>
                      </table>
                      <div className="table-meta-bar">
                        <span>Showing 1–{filteredNotifications.length} of {filteredNotifications.length} notifications</span>
                        <span>Base currency: USD</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default Analytics;
